import React, {useContext} from "react";
import ScorePill from "./ScorePill";
import Color from "color";
import clsx from "clsx";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {RequireAtLeastOne} from "../../utils/utils";
import {
	LeaderboardContentSize,
	LeaderboardContentSizeCompact,
	LeaderboardRowHeight,
	LeaderboardRowHeightXxlProportion,
	LeaderboardRowHeightCompact,
	LeaderboardRowHeightCompactXxlProportion,
} from "../accessory/accessoryConstants";
import RankArrowIcon from "../common/RankArrowIcon";
import RankArrowTile from "../common/RankArrowTile";
import {EngageAppContext} from "../../App";
import {TeamMode} from "engage-common/types";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import {useWindowSize} from "engage-client/hooks/useResizeObserver";

const useStyles = createUseStyles<Theme>((theme) => ({
	leaderboardRow: {
		flex: 1,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: props => props.rowHeight,
		borderTopLeftRadius: props => props.rowHeight / 2,
		borderBottomLeftRadius: props => props.rowHeight / 2,
		backgroundColor: props => props.backgroundColor,
		borderWidth: props => props.currentPlayer ? theme.accessoryPanel.leaderboard.row.currentPlayer.borderWidth : theme.accessoryPanel.leaderboard.row.borderWidth,
		borderColor: props => props.borderColor,
		borderStyle: "solid",
		paddingRight: 2,
		color: props => props.color
	},

	rank: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		margin:  props => (props.rowHeight - props.contentSize) / 2,
		height:  props => props.contentSize,
		width: props => props.contentSize,
		...theme.accessoryPanel.leaderboard.rank.showBorder ? {
			borderRadius: props => props.contentSize / 2,
			borderColor: props => props.highlightColor,
			borderWidth: props => props.rankBorderWidth,
			borderStyle: "solid"} : {},
	},

	name: {
		flex: 1,
		margin: props => (props.rowHeight - props.contentSize) / 2,
		marginLeft: 16,
		[theme.breakpoints.down("lg")]: {
			marginLeft: 8,
		},
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
	},

	rankChange: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		width: 50,
		marginLeft: 1,
		color: props => props.rankChangeColor,
	},

	arrow: {
		width: 16,
		height: 20,
		paddingBottom: 3,
		paddingRight:2,
		transform: props => props.rankChange < 0 ? "scaleY(-1)" : ""
	},

	hotStreak: {
		width: 16,
		height: 26,
		paddingBottom: theme.accessoryPanel.leaderboard.row.leaderboardTiles ? 0 : 4,
		margin: theme.accessoryPanel.leaderboard.row.leaderboardTiles ? "auto" : "0 2px 0 0"
	},
}));

export type LRProps = {
	color?: string
	backgroundColor: string
	highlightColor: string
	borderColor: string
	highlighted: boolean
	rank: string
	rankChange: number
	score: number
	name: string
	className?: string
	extraStyles?: any
	currentPlayer: boolean
	compact: boolean
	hotStreak: boolean
	onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
	onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
}

export type LeaderboardRowProps = RequireAtLeastOne<LRProps,"backgroundColor" | "highlightColor">

export default function LeaderboardRow(partial:LeaderboardRowProps) {
	const theme:Theme = useTheme();
	const isXxl = useMediaQuery(theme.breakpoints.up("xxl"));
	const windowWidth = useWindowSize().width;
	let leaderboardRowHeightXxl = -1;
	let leaderboardRowHeightCompactXxl = -1;
    if (windowWidth) {
        leaderboardRowHeightXxl = (LeaderboardRowHeightXxlProportion * windowWidth) / 100;
		leaderboardRowHeightCompactXxl = (LeaderboardRowHeightCompactXxlProportion * windowWidth) / 100;
    }
	const {lobby} = useContext(EngageAppContext.current);
	const teamMode = lobby?.teamMode !== TeamMode.Off;
	const props = convertToLRProps(partial);
	// eslint-disable-next-line @typescript-eslint/no-extra-parens
	const rankBorderWidth = props.highlighted ? (props.compact ? 2 : 3) : 0;
	let rankChangeColor:string;
	if(props.currentPlayer) {
		if(teamMode) {
			props.borderColor = theme.accessoryPanel.leaderboard.row.currentPlayer.team.borderColor;
			rankChangeColor = props.highlighted ? theme.accessoryPanel.leaderboard.row.currentPlayer.team.rankChangeRankedColor :
				theme.accessoryPanel.leaderboard.row.currentPlayer.team.rankChangeUnrankedColor;
		} else {
			props.borderColor = theme.accessoryPanel.leaderboard.row.currentPlayer.borderColor;
			rankChangeColor = props.highlighted ? theme.accessoryPanel.leaderboard.row.currentPlayer.rankChangeRankedColor :
				theme.accessoryPanel.leaderboard.row.currentPlayer.rankChangeUnrankedColor;
		}

	} else {
		rankChangeColor = props.rankChange > 0 ? theme.accessoryPanel.leaderboard.rankChange.up : theme.accessoryPanel.leaderboard.rankChange.down;
	}
	const contentSize = props.compact ? LeaderboardContentSizeCompact : LeaderboardContentSize;
	const rowHeight = props.compact ? isXxl ? leaderboardRowHeightCompactXxl : LeaderboardRowHeightCompact : isXxl ? leaderboardRowHeightXxl : LeaderboardRowHeight;

	const classes = useStyles({
		...props,
		contentSize,
		rowHeight,
		rankBorderWidth,
		rankChangeColor,
	});
	const rootClass = clsx({
		[classes.leaderboardRow]: true,
		[props.className ?? ""]: true,
	});

	//Increase fontWeight if this is the current player
	const fontWeight = props.currentPlayer ? 700 : undefined;

	return <div className={rootClass} style={props.extraStyles} onMouseEnter={props.onMouseEnter} onMouseLeave={props.onMouseLeave}>
		<DisplayText className={classes.rank} variant={TextVariant.s18med} fontWeight={fontWeight}>{props.rank}</DisplayText>
		<DisplayText className={classes.name} variant={TextVariant.s18med} lineHeight={1.12} fontWeight={fontWeight}>{props.name}</DisplayText>
		{
			props.hotStreak ?
				<>
				{props.highlighted ?
					<img src={theme.accessoryPanel.leaderboard.row.rankedStreakSVG} className={classes.hotStreak} alt="Hot Streak - 3 correct questions in a row" /> 
				:
					<img src={theme.accessoryPanel.leaderboard.row.unrankedStreakSVG} className={classes.hotStreak} alt="Hot Streak - 3 correct questions in a row" />
				}
				</>
			 : null
		}
		<ScorePill color={props.highlightColor} highlighted={props.highlighted} score={props.score} compact={props.compact} fontWeight={fontWeight}/>
		<div className={classes.rankChange}>
			{props.rankChange !== 0 &&
					<>
					{theme.accessoryPanel.leaderboard.row.leaderboardTiles ?
					<RankArrowTile number={props.rankChange}/> :
					<>
					<RankArrowIcon className={classes.arrow} color={rankChangeColor}/>
					<DisplayText variant={TextVariant.s18med} fontWeight={fontWeight}>{Math.abs(props.rankChange)}</DisplayText>
					</>
					}
					</>
			}
		</div>
	</div>;
};

export function convertToLRProps(props:LeaderboardRowProps):LRProps {
	const highlightColor = props.highlightColor ?? "rgba(0,0,0,0)";
	const backgroundColor = props.backgroundColor ?? Color(props.highlightColor).alpha(0.3).toString();
	return {
		...props,
		highlightColor,
		backgroundColor
	};
}
